import { configurationSources, loadConfiguration } from './rahlle-config.js';

if (!customElements.get('rahlle-product')) {
  customElements.define('rahlle-product', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
      this.form = this.querySelector('.rp-form');
      this.select = this.querySelector('[data-variant-select]');
      this.quantity = this.form.elements.quantity;
      this.add = this.querySelector('.rp-add');
      this.message = this.querySelector('[data-form-message]');
      this.fields = [...this.querySelectorAll('.rp-option')];
      this.photos = [...this.querySelectorAll('[data-gallery-image]')];
      this.configThumb = this.querySelector('[data-config-thumb]');
      this.galleryItems = [...(this.configThumb ? [this.configThumb] : []), ...this.photos];
      this.galleryIndex = this.configThumb ? 0 : Math.max(0, this.photos.findIndex(photo => photo.hasAttribute('aria-current')));
      this.querySelector('.rp-native-variant').hidden = true;
      this.querySelector('.rp-options').hidden = false;
      this.querySelector('.rp-gallery-controls').hidden = this.galleryItems.length < 2;
      if (this.configThumb) this.configThumb.hidden = false;
      this.fields.forEach(field => field.addEventListener('change', () => this.updateVariant(true), { signal }));
      this.galleryItems.forEach((item, index) => item.addEventListener('click', event => {
        event.preventDefault();
        this.showImage(index);
      }, { signal }));
      this.querySelectorAll('[data-gallery-step]').forEach(button => button.addEventListener('click', () => {
        this.showImage((this.galleryIndex + Number(button.dataset.galleryStep) + this.galleryItems.length) % this.galleryItems.length);
      }, { signal }));
      this.querySelectorAll('[data-quantity-step]').forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => {
          const step = Number(this.quantity.step) || 1;
          const min = Number(this.quantity.min) || 1;
          const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
          this.quantity.value = Math.min(max, Math.max(min, (Number(this.quantity.value) || min) + Number(button.dataset.quantityStep) * step));
          this.updateQuantityButtons();
        }, { signal });
      });
      this.quantity.addEventListener('input', () => this.updateQuantityButtons(), { signal });
      this.form.addEventListener('submit', event => this.addToCart(event), { signal });
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const variant = this.config.variants.find(v => String(v.id) === id);
        if (variant) this.selectVariant(variant);
      }, { signal });
      this.setupTabs(signal);
      this.setupFeatures(signal);
      this.updateVariant(false);
      this.showImage(this.galleryIndex);
    }
    disconnectedCallback() { this.controller?.abort(); this.layerRequest = null; }
    selectedOptions() {
      const options = [...(this.config.variants.find(v => String(v.id) === this.select.value)?.options || [])];
      const parts = {};
      this.fields.forEach(field => {
        const value = field.querySelector('input:checked')?.value;
        field.querySelector('[data-option-label]').textContent = value || '';
        if (field.dataset.optionPart) parts[field.dataset.optionPart] = value;
        else options[Number(field.dataset.optionIndex)] = value;
      });
      if (parts.side && parts.wood) options[this.config.options.indexOf('Side and Wood')] = `${parts.side} / ${parts.wood}`;
      return options;
    }
    selectVariant(variant) {
      this.select.value = String(variant.id);
      this.fields.forEach(field => {
        let value = variant.options[Number(field.dataset.optionIndex)];
        if (field.dataset.optionPart) value = value.split(' / ')[field.dataset.optionPart === 'side' ? 0 : 1];
        field.querySelectorAll('input').forEach(input => { input.checked = input.value === value; });
      });
      this.updateVariant(false);
    }
    updateVariant(changeUrl) {
      const options = this.selectedOptions();
      const variant = this.config.variants.find(v => v.options.every((value, index) => value === options[index]));
      this.variant = variant;
      this.select.value = variant ? String(variant.id) : '';
      this.add.disabled = !variant?.available || this.busy;
      this.querySelector('[data-add-label]').textContent = !variant ? 'Unavailable' : variant.available ? 'Add to cart' : 'Sold out';
      this.querySelector('[data-stock]').textContent = !variant ? 'This combination is unavailable' : variant.available ? 'In stock — ready for your next ride' : 'Currently out of stock';
      this.querySelector('[data-stock]').classList.toggle('is-sold-out', !variant?.available);
      this.querySelector('[data-price]').textContent = variant?.price || '—';
      const compare = this.querySelector('[data-compare]');
      compare.hidden = !variant?.compare;
      compare.textContent = variant?.compare || '';
      if (variant) {
        this.quantity.min = variant.min;
        this.quantity.step = variant.step;
        if (variant.max !== null) this.quantity.max = variant.max;
        else this.quantity.removeAttribute('max');
        this.quantity.value = Math.min(variant.max ?? Infinity, Math.max(variant.min, variant.min + Math.floor(((Number(this.quantity.value) || variant.min) - variant.min) / variant.step) * variant.step));
        if (changeUrl) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(null, '', url);
        }
        if (this.config.configuration) this.updateLayers(options, changeUrl);
        else if (changeUrl && variant.imageId) {
          const index = this.photos.findIndex(photo => photo.dataset.imageId === String(variant.imageId));
          if (index >= 0) this.showImage(index);
        }
      }
      this.updateQuantityButtons();
    }
    updateQuantityButtons() {
      this.querySelector('[data-quantity-step="-1"]').disabled = Number(this.quantity.value) <= Number(this.quantity.min);
      this.querySelector('[data-quantity-step="1"]').disabled = Boolean(this.quantity.max) && Number(this.quantity.value) >= Number(this.quantity.max);
    }
    async updateLayers(options, showConfig) {
      const request = {};
      this.layerRequest = request;
      // Keep the displayed stack until every replacement layer is decoded.
      // Hiding it here exposes the default gallery photo between finishes.
      try {
        const sources = configurationSources(this.config.configuration, this.config.options, options);
        const images = await loadConfiguration(sources);
        if (this.layerRequest !== request) return;
        this.querySelector('.rp-config').replaceChildren(...images);
        this.configReady = true;
        this.querySelector('.rp-config').setAttribute('aria-label', `${this.querySelector('h1').textContent}: ${options.join(', ')}`);
        if (showConfig || this.galleryIndex === 0) this.showImage(0);
      } catch {
        if (this.layerRequest !== request) return;
        this.configReady = false;
        const photoIndex = this.photos.findIndex(photo => photo.dataset.imageId === String(this.variant?.imageId));
        if (this.photos.length) this.showImage(1 + Math.max(0, photoIndex));
        else this.showImage(0);
      }
    }
    showImage(index) {
      if (!this.galleryItems.length) return;
      this.galleryIndex = index;
      const item = this.galleryItems[index];
      const isConfig = item === this.configThumb;
      const config = this.querySelector('.rp-config');
      if (config) config.hidden = !isConfig || !this.configReady;
      const main = this.querySelector('.rp-main-image');
      main.hidden = isConfig && Boolean(this.configReady);
      if (!isConfig && main.tagName === 'IMG') {
        main.removeAttribute('srcset');
        main.src = item.dataset.galleryImage;
        main.alt = item.dataset.imageAlt;
      }
      this.galleryItems.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.querySelector('[data-gallery-counter]').textContent = `${index + 1} / ${this.galleryItems.length}`;
    }
    setupTabs(signal) {
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      if (!tabs.length) return;
      const panels = [...this.querySelectorAll('.rp-panel')];
      this.querySelector('.rp-tabs').hidden = false;
      const activate = index => {
        tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
        panels.forEach((panel, i) => { panel.hidden = i !== index; panel.setAttribute('role', 'tabpanel'); });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (['ArrowDown', 'ArrowRight'].includes(event.key)) next = (index + 1) % tabs.length;
          if (['ArrowUp', 'ArrowLeft'].includes(event.key)) next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); activate(next); tabs[next].focus(); }
        }, { signal });
      });
      activate(0);
    }
    setupFeatures(signal) {
      const carousel = this.querySelector('[data-feature-carousel]');
      if (!carousel) return;
      const slides = [...carousel.querySelectorAll('[data-feature-slide]')];
      carousel.classList.add('is-ready');
      const dotContainer = carousel.querySelector('[data-feature-dots]');
      dotContainer.replaceChildren();
      let current = 0;
      const activate = index => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => { slide.hidden = i !== current; });
        dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === current)));
        carousel.querySelector('[data-feature-status]').textContent = `${current + 1} of ${slides.length}: ${slides[current].getAttribute('aria-label')}`;
      };
      const dots = slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show feature ${index + 1}: ${slide.getAttribute('aria-label')}`);
        dot.addEventListener('click', () => activate(index), { signal });
        dotContainer.append(dot);
        return dot;
      });
      carousel.querySelectorAll('[data-feature-step]').forEach(button => button.addEventListener('click', () => activate(current + Number(button.dataset.featureStep)), { signal }));
      dotContainer.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = current + 1;
        if (event.key === 'ArrowLeft') next = current - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = slides.length - 1;
        if (next !== undefined) { event.preventDefault(); activate(next); dots[current].focus(); }
      }, { signal });
      carousel.querySelector('[data-feature-controls]').hidden = slides.length < 2;
      activate(0);
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.variant?.available || !this.form.reportValidity()) return;
      this.busy = true;
      this.add.disabled = true;
      this.add.setAttribute('aria-busy', 'true');
      this.message.hidden = false;
      this.message.textContent = 'Adding to your cart…';
      try {
        const response = await fetch(this.config.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add this item. Please try again.');
        this.message.textContent = 'Added to your cart.';
        this.querySelector('.rp-view-cart').hidden = false;
        // Cart count refresh is separate: a failed refresh must never retry a successful add.
        try {
          const cartResponse = await fetch(this.config.cartUrl);
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('.rh-count').forEach(el => { el.textContent = cart.item_count; });
          }
        } catch { /* Native cart navigation still shows the authoritative contents. */ }
      } catch (error) {
        this.message.textContent = error.message || 'Unable to reach the store. Check your cart before trying again.';
      } finally {
        this.busy = false;
        this.add.removeAttribute('aria-busy');
        this.add.disabled = !this.variant?.available;
      }
    }
  });
}
